import { error as logErrorToConsole } from '@ms/yammer-console-logging';
import { getTelemetryClientConfig } from '@ms/yammer-telemetry-store';

import type {
  AnalyticsV2Event,
  AnalyticsV2EventInput,
  LogEventInput,
  LogEventWithOptions,
  ReportAnalyticsEventsInput,
  ReportLogEventsInput,
} from '../events';

import { isKeepaliveRequestBodyWithinLimit } from './getLogEventsWithinKeepaliveRequestBodyLimit';
import { loadProtobufRequestFunctions } from './protobuf/request';
import type { GetEventHeadersWithAuthentication, GetEventHeadersWithoutAuthentication } from './reportHeaders';
import {
  getEventHeadersForPageHide,
  getEventHeadersWithAuthentication,
  getEventHeadersWithoutAuthentication,
} from './reportHeaders';

const UnknownClientId = 'UNKNOWN';
const LogEventApiPath = '/api/v1/yamalytics/webui';
const AnalyticsEventApiPath = '/api/v2/events';
const AnalyticsV2EventApiPath = '/api/v3/events';

const createJsonRequestBody = (events: LogEventInput[] | ReportAnalyticsEventsInput) => JSON.stringify(events);

const createRequestOptions = (
  headers: Headers,
  events: LogEventInput[] | ReportAnalyticsEventsInput,
  keepalive = false
) => ({
  headers,
  method: 'POST',
  body: createJsonRequestBody(events),
  ...(keepalive && { keepalive }),
});

const createAnalyticsV2RequestOptions = async (headers: Headers, events: AnalyticsV2Event[]) => {
  const { getProtobufEventRequestBody } = await loadProtobufRequestFunctions();

  return {
    headers,
    method: 'POST',
    body: getProtobufEventRequestBody(events),
  };
};

const createUrl = (host: string, path: string) => `${host}${path}`;

const createHeaders = (headers: Record<string, string>) =>
  new Headers({
    ...headers,
    accept: 'application/json',
    'content-type': 'application/json',
  });

class BadFetchResponseError extends Error {
  constructor(response: Response, details: string) {
    super(`Fetch failed for ${response.url} with status ${response.statusText} with details ${details}`);
  }
}

const getBadFetchResponseDetails = async (response: Response) => {
  try {
    return await response.text();
  } catch (error) {
    return `Failed to read response details: ${String(error)}`;
  }
};

const throwErrorOnBadFetchResponse = async (response: Response) => {
  if (!response.ok) {
    throw new BadFetchResponseError(response, await getBadFetchResponseDetails(response));
  }
};

const logTelemetryReportingFailure = (error: unknown) => {
  logErrorToConsole(`Telemetry reporting failed. Details: ${JSON.stringify(error, Object.getOwnPropertyNames(error))}`);
};

const executeFetchRequest = async (url: string, requestOptions: RequestInit) => {
  const response = await fetch(url, requestOptions);
  await throwErrorOnBadFetchResponse(response);
};

type ReportTelemetryEventsToServer = (requestOptions: RequestInit, apiPath: string) => Promise<void>;
const reportTelemetryEventsToServer: ReportTelemetryEventsToServer = async (requestOptions, apiPath) => {
  const clientConfig = getTelemetryClientConfig();
  if (!clientConfig?.telemetryHost) {
    return;
  }

  const url = createUrl(clientConfig.telemetryHost, apiPath);
  await executeFetchRequest(url, requestOptions);
};

type ReportLogEventsNow = (logEventsInput: ReportLogEventsInput) => Promise<void>;
export const reportLogEventsNow: ReportLogEventsNow = async (logEventsInput) => {
  const clientConfig = getTelemetryClientConfig();
  const allEvents: LogEventWithOptions[] = [
    ...logEventsInput.errorEvents,
    ...logEventsInput.performanceEvents,
    ...logEventsInput.infoEvents,
  ];

  if (clientConfig?.telemetryClientId === UnknownClientId) {
    logErrorToConsole('Unexpected telemetryClientId = UNKNOWN');

    return;
  }

  try {
    await reportLogEventsToServer(allEvents);
  } catch (err) {
    logTelemetryReportingFailure(err);
  }
};

type ReportLogEventsWithKeepalive = (logEventInputs: LogEventInput[]) => boolean;
export const reportLogEventsWithKeepalive: ReportLogEventsWithKeepalive = (logEventInputs) => {
  const clientConfig = getTelemetryClientConfig();

  if (clientConfig?.telemetryClientId === UnknownClientId) {
    logErrorToConsole('Unexpected telemetryClientId = UNKNOWN');

    return false;
  }

  if (!clientConfig?.telemetryHost) {
    return false;
  }

  if (logEventInputs.length === 0 || !isKeepaliveRequestBodyWithinLimit(logEventInputs)) {
    return false;
  }

  void reportTelemetryEventsToServer(
    createRequestOptions(createHeaders(getEventHeadersForPageHide()), logEventInputs, true),
    LogEventApiPath
  ).catch((error: unknown) => {
    logTelemetryReportingFailure(error);
  });

  return true;
};

type ReportLogEventWithKeepalive = (logEventInput: LogEventInput) => boolean;
export const reportLogEventWithKeepalive: ReportLogEventWithKeepalive = (logEventInput) =>
  reportLogEventsWithKeepalive([logEventInput]);

type ReportAnalyticsEventsNow = (analyticsEventsInput: ReportAnalyticsEventsInput) => Promise<void>;
export const reportAnalyticsEventsNow: ReportAnalyticsEventsNow = (analyticsEventsInput) =>
  reportTelemetryEventsNow({ events: analyticsEventsInput, eventInputType: 'Analytics' });

type ReportAnalyticsV2EventsNow = (analyticsEventsInput: AnalyticsV2EventInput[]) => Promise<void>;
export const reportAnalyticsV2EventsNow: ReportAnalyticsV2EventsNow = (analyticsEventsInput) =>
  reportTelemetryEventsNow({ events: analyticsEventsInput, eventInputType: 'AnalyticsV2' });

const groupLogEventsBySkipAuthentication = (eventsWithOptions: LogEventWithOptions[]) =>
  eventsWithOptions.reduce<Record<string, LogEventInput[]>>(
    (acc, { event, options }) => {
      const key = options?.skipAuthentication ? 'true' : 'false';
      acc[key].push(event);

      return acc;
    },
    { false: [], true: [] }
  );

type GetLogEventHeaders = GetEventHeadersWithAuthentication | GetEventHeadersWithoutAuthentication;
const reportLogEventGroupToServer = async (events: LogEventInput[], getLogEventHeaders: GetLogEventHeaders) => {
  if (events.length === 0) {
    return;
  }

  await reportTelemetryEventsToServer(
    createRequestOptions(createHeaders(await getLogEventHeaders()), events),
    LogEventApiPath
  );
};

const reportLogEventsToServer = async (eventsWithOptions: LogEventWithOptions[]) => {
  const groupedEvents = groupLogEventsBySkipAuthentication(eventsWithOptions);

  await Promise.all([
    reportLogEventGroupToServer(groupedEvents.false, getEventHeadersWithAuthentication),
    reportLogEventGroupToServer(groupedEvents.true, getEventHeadersWithoutAuthentication),
  ]);
};

interface ReportAnalyticsEventsNowProps {
  readonly eventInputType: 'Analytics';
  readonly events: ReportAnalyticsEventsInput;
}

interface ReportAnalyticsV2EventsNowProps {
  readonly eventInputType: 'AnalyticsV2';
  readonly events: AnalyticsV2EventInput[];
}

type ReportTelemetryEventsNowProps = ReportAnalyticsEventsNowProps | ReportAnalyticsV2EventsNowProps;

type ReportTelemetryEventsNow = (props: ReportTelemetryEventsNowProps) => Promise<void>;
const reportTelemetryEventsNow: ReportTelemetryEventsNow = async (props) => {
  const clientConfig = getTelemetryClientConfig();
  if (clientConfig?.telemetryClientId === UnknownClientId) {
    logErrorToConsole('Unexpected telemetryClientId = UNKNOWN');

    return;
  }

  try {
    if (props.eventInputType === 'Analytics') {
      const fetchHeaders = createHeaders(await getEventHeadersWithAuthentication());
      const requestOptions = createRequestOptions(fetchHeaders, props.events);

      await reportTelemetryEventsToServer(requestOptions, AnalyticsEventApiPath);
    } else {
      const fetchHeaders = createHeaders(await getEventHeadersWithAuthentication());
      const requestOptions = await createAnalyticsV2RequestOptions(fetchHeaders, props.events);

      await reportTelemetryEventsToServer(requestOptions, AnalyticsV2EventApiPath);
    }
  } catch (err) {
    logTelemetryReportingFailure(err);
  }
};
